import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { formatDistanceToNow } from 'date-fns';

import { localDate } from '@iconik-io/core/src/date/format';
import type { AssetsShareElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { FIELD_TYPES } from '@iconik-io/shared-lib/src/constants/search';

import CollapsibleItems from '../../components/collapsible_items/collapsible_items';
import CollapsibleTableRow from '../../components/collapsible_items/table_row_renderer';
import CopyButton from '../../components/copy_button/copy_button';
import DebugDumpEntity from '../../components/debug_dump_entity/debug_dump_entity';
import DeleteShareUsersModal from '../../components/modals/delete_objects/share_users';
import DeleteSharesModal from '../../components/modals/delete_objects/shares';
import ModalsManager from '../../components/modals/modals_manager/modals_manager';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import SelectableItems from '../../components/selectable_items/selectable_items';
import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import { SHARES } from '../../constants/help_urls';
import {
  DELETE_SHARE_USERS_MODAL,
  DELETE_SHARES_MODAL,
  SHARE_MODAL,
} from '../../constants/modals';
import { COLLECTION_PAGE_ROUTE } from '../../constants/routes';
import { createListItemsSelector } from '../../ducks/common_list/selectors';
import { commonListActions } from '../../ducks/common_list/slice';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  getAssetSharesAll,
  getCollectionSharesAll,
  getSharesRecursive,
} from '../../ducks/shares/thunks';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import TableView from '../../ui/table/table';
import type { TableRowProps } from '../../ui/table/table_row';
import type { Column } from '../../ui/table/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import { addObjectType } from '../../utils/object_type';
import { localeSortByKeyDesc } from '../../utils/sort';
import SharePanelContextMenu from './context_menu';
import { ShareObjectColumn } from './share_object_column';
import { ShareUsers } from './share_users/share_users';

import styles from './shares.module.css';

const columns: Column<AssetsShareElasticSchemaWithIdNullable>[] = [
  {
    label: 'Expand share',
    hideLabel: true,
    name: 'id',
    type: FIELD_TYPES.OPENER,
    style: { width: 30 },
  },
  {
    label: 'Share owner',
    hideLabel: true,
    name: 'owner_id',
    type: FIELD_TYPES.USER,
    style: { width: 40 },
  },
  {
    name: 'date_created',
    label: 'Created',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => {
      let element = null;
      if (item.date_created) {
        element = formatDistanceToNow(localDate(item.date_created), {
          addSuffix: true,
        });
      }
      return (
        <>
          <DebugDumpEntity entityKey="shares" id={item.id} />
          {element}
        </>
      );
    },
    style: { width: '50%' },
  },
  {
    name: 'expires',
    label: 'Expires',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => {
      if (!item.expires) {
        return null;
      }
      return formatDistanceToNow(localDate(item.expires), { addSuffix: true });
    },
    style: { width: '50%' },
  },
  {
    label: 'Share type',
    hideLabel: true,
    name: 'object_id',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ShareObjectColumn,
  },
  {
    name: 'link',
    label: 'Share URL',
    hideLabel: true,
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) =>
      item.url && (
        <CopyButton
          className={styles.copy_button}
          copy={item.url}
          placement="top"
          iconName="link"
          text={'Copy URL'}
        />
      ),
  },
  {
    label: 'Share actions',
    hideLabel: true,
    name: 'object_type',
    type: FIELD_TYPES.MENU,
  },
];

const itemsSelector =
  createListItemsSelector<AssetsShareElasticSchemaWithIdNullable>();

const { resetListData } = commonListActions('panel_shares');

type SharesTableRowProps =
  TableRowProps<AssetsShareElasticSchemaWithIdNullable> & {
    expandedIds: string[];
    entity: string;
    entityId: string;
    filteredShares: AssetsShareElasticSchemaWithIdNullable[];
  };

const SharesTableRow = (props: SharesTableRowProps) => (
  <CollapsibleTableRow {...props} ids={props.expandedIds} key={props.id}>
    <ShareUsers
      shareId={props.id}
      colSpan={columns.length}
      shares={props.filteredShares}
    />
  </CollapsibleTableRow>
);

export function SharesPanel(): ReactNode {
  const dispatch = useAppDispatch();
  // Route and id come from the same match: see PanelsContainer.
  const { route, params } = useLeafRouteParams();
  const entityId = params.id;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const ids = useAppSelector((state) => state.panelsState.shares.ids);
  const loading = useAppSelector((state) => state.panelsState.shares.loading);
  const shareEntities = useAppSelector((state) => state.entities.shares);
  const collection = useAppSelector((state) =>
    entityId ? state.entities.collections[entityId] : undefined,
  );
  const asset = useAppSelector((state) => state.pages.asset.asset);
  const roles = useAppSelector(selectUserRoles);
  const entity =
    route === COLLECTION_PAGE_ROUTE
      ? OBJECT_TYPE_COLLECTIONS
      : OBJECT_TYPE_ASSETS;

  const shares = localeSortByKeyDesc(
    itemsSelector(shareEntities, ids),
    'date_created',
  );

  // Refetch whenever the entity in the URL changes, and clear the shared list
  // slice on the way out so the next entity never shows the previous one.
  useEffect(() => {
    if (!entityId) {
      return undefined;
    }
    dispatch(resetListData());
    if (entity === OBJECT_TYPE_ASSETS) {
      dispatch(getAssetSharesAll(entityId));
    } else if (entity === OBJECT_TYPE_COLLECTIONS) {
      dispatch(getCollectionSharesAll(entityId));
    } else {
      dispatch(getSharesRecursive(entity, entityId, { per_page: 100 }));
    }
    return () => {
      dispatch(resetListData());
    };
  }, [entityId, entity]);

  const createShare = useCallback(() => {
    const objectToShare =
      entity === OBJECT_TYPE_COLLECTIONS ? collection : asset;
    dispatch(
      openModal({
        id: SHARE_MODAL,
        contextItems: [addObjectType(objectToShare, entity)],
      }),
    );
  }, [entity, collection, asset]);

  const getMessage = (): ReactElement => {
    const entityName = entity === 'collections' ? 'Collection' : 'Asset';
    const isBlockedAsset = entity === OBJECT_TYPE_ASSETS && asset.is_blocked;
    const canWriteShares = roles.canWriteShares && !isBlockedAsset;

    if (shares.length > 0) {
      return (
        <div className={styles.button_container}>
          <TooltipTrigger
            tooltipText="You cannot share blocked assets"
            placement="left"
            shouldShowTooltip={() => asset.is_blocked === true}
          >
            <Button
              disabled={!canWriteShares}
              onClick={createShare}
              noBorder
              iconName="share"
            >
              Share
            </Button>
          </TooltipTrigger>
        </div>
      );
    }

    return (
      <NoContentPanel
        title={
          entityName === 'Collection'
            ? 'Share collections to give others access to view, download, and upload content directly'
            : 'Share assets to give others access to view or download content directly'
        }
        linkText="Learn more"
        linkHref={SHARES}
        actionText="SHARE"
        onActionClick={canWriteShares ? createShare : undefined}
      />
    );
  };

  // TODO: Remove FE filtering once API supports filtering in Shares panel.
  // https://linear.app/iconik/issue/RP-2440/filter-shares-with-automatic-approval-share-on-be-for-shares-panel
  const filteredShares = shares.filter(
    (share) => !share.automatic_approval_share,
  );

  if (loading) {
    return <LoadingBar className={styles.loading} />;
  }

  if (!entityId) {
    return null;
  }

  if (filteredShares.length === 0) {
    return getMessage();
  }

  return (
    <div className={styles.root}>
      <ModalsManager
        modals={{
          [DELETE_SHARES_MODAL]: DeleteSharesModal,
          [DELETE_SHARE_USERS_MODAL]: DeleteShareUsersModal,
        }}
      />
      <CollapsibleItems items={expandedIds} onExpand={setExpandedIds}>
        <SelectableItems
          onSelectIds={setSelectedIds}
          items={filteredShares}
          multiselect={false}
          isSelectAllAllowed={false}
          contextMenu={SharePanelContextMenu}
        >
          <TableView
            className={styles.table}
            items={filteredShares}
            selectedIds={selectedIds}
            renderColumns={columns}
            tableRowComponent={SharesTableRow}
            tableRowComponentProps={{
              entity,
              entityId,
              filteredShares,
              expandedIds,
            }}
          />
          {roles.canWriteShares && getMessage()}
        </SelectableItems>
      </CollapsibleItems>
    </div>
  );
}

export default SharesPanel;
